import { Money } from '../money/money';
import { DEFAULT_ROUNDING } from '../money/rounding';
import { applyDiscount, computeLineItem } from './lineItem';
import { applyTax } from './tax';
import type { InvoiceInput, InvoiceTotals, LineItemResult } from './types';

/**
 * The single source of truth for invoice money math. Both client-side
 * preview and server-side authoritative computation call this.
 *
 * The algorithm:
 *   1. Per line: compute gross / line-discount / net / taxable.
 *   2. Sum line nets → `subtotal` (pre-invoice-discount).
 *   3. Apply invoice-level discount to subtotal →
 *      `taxableSubtotal` (post-invoice-discount subtotal).
 *   4. Delegate to the tax engine, which derives its own "taxable base"
 *      from the per-line taxable amounts scaled by the invoice-discount
 *      ratio — non-taxable lines never contribute.
 *   5. `total`:
 *        - inclusive tax modes: total = taxableSubtotal
 *          (gross is preserved; only the breakdown splits tax off).
 *        - exclusive / as_deduction: total = taxableSubtotal + tax.total
 *          (tax.total is negative for deductions).
 *   6. `amountDue = total − amountPaid`. Negative values represent
 *      over-payment and are not clamped — the caller decides.
 */
export function computeInvoiceTotals(input: InvoiceInput): InvoiceTotals {
  const mode = input.rounding ?? DEFAULT_ROUNDING;
  const currency = input.currency;

  const lines: LineItemResult[] = input.lineItems.map((li) =>
    computeLineItem(li, currency, mode),
  );

  const subtotal = Money.sum(
    lines.map((l) => l.net),
    currency,
  );
  const lineDiscountTotal = Money.sum(
    lines.map((l) => l.discount),
    currency,
  );

  const invoiceDiscount = applyDiscount(subtotal, input.invoiceDiscount, mode);
  const discountTotal = Money.add(lineDiscountTotal, invoiceDiscount);
  const discountedSubtotalUnclamped = Money.sub(subtotal, invoiceDiscount);
  // `applyDiscount` already clamps the discount to the base, so this
  // should never go negative. Guard anyway — cheaper than a debugging
  // bug.
  const taxableSubtotal = Money.isNegative(discountedSubtotalUnclamped)
    ? /* v8 ignore next */ Money.zero(currency)
    : discountedSubtotalUnclamped;

  const tax = applyTax(
    input.tax,
    lines,
    input.lineItems,
    subtotal,
    taxableSubtotal,
    currency,
    mode,
  );

  const isInclusive =
    (tax.method === 'on_total' && tax.inclusive) ||
    (tax.method === 'per_item' && tax.inclusive);
  const total = isInclusive
    ? taxableSubtotal
    : Money.add(taxableSubtotal, tax.total);

  const amountPaid = input.amountPaid ?? Money.zero(currency);
  if (amountPaid.currency !== currency) {
    throw new Error(
      `computeInvoiceTotals: amountPaid currency ${amountPaid.currency} does not match invoice currency ${currency}`,
    );
  }
  const amountDue = Money.sub(total, amountPaid);

  return {
    currency,
    subtotal,
    discountTotal,
    taxableSubtotal,
    tax,
    total,
    amountPaid,
    amountDue,
    lines,
  };
}
